import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth0, User } from '@auth0/auth0-react';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { getConsumerToggleOptions } from './api/translate';
import { ConsumerToggle, ConsumerToggleResponse } from './types/ConsumerToggle';

interface LoginState {
	user?: User;
	auth0IdToken: string | null;
	selectedConsumer: string | null;
	isLoading: boolean;
	isAppInitialized: boolean;
	rnResponseProjectId: string | null;
	error: Error | null;
	consumerToggleOptions: ConsumerToggle[];
	consumerId: string | null;
	iFrameQueryParam: string | null;
}

interface LoginActions {
	setRnResponseProjectId: (projectId: string | null) => void;
	setAuth0IdToken: (token: string | null) => void;
	setIsAppInitialized: (isAppInitialized: boolean) => void;
	setSelectedConsumer: (consumer: string | null) => void;
	setSelectedConsumerId: (consumerId: string | null) => void;
	setIFrameQueryParam: (sId: string | null) => void;
	refreshToken: () => Promise<void>;
	initConsumerToggleOptions: (auth0IdToken: string | null) => Promise<ConsumerToggleResponse | null>;
}

interface LoginContextType extends LoginState, LoginActions {}

type LoginProviderProps = {
	children: React.ReactNode;
}

const initialState: LoginState = {
	user: undefined,
	auth0IdToken: null,
	selectedConsumer: localStorage.getItem('selectedConsumer') ?? null,
	isLoading: true,
	isAppInitialized: false,
	rnResponseProjectId: null,
	error: null,
	consumerToggleOptions: [],
	consumerId: localStorage.getItem('consumerId') ?? null,
	iFrameQueryParam: localStorage.getItem('iFrameQueryParam') ?? null,
};

const LoginContext = createContext<LoginContextType | null>(null);

export const useAuthData = () => {
	const context = useContext(LoginContext);
	if (!context) {
		throw new Error('useAuthData must be used within a LoginProvider');
	}
	return context;
};

const isTokenExpired = (token: JwtPayload): boolean => {
	const currentTime = Math.floor(Date.now() / 1000);
	return token.exp! < currentTime;
};

const TOKEN_REFRESH_MARGIN = 60; // seconds before expiration to refresh

const shouldRefreshToken = (token: JwtPayload): boolean => {
	const currentTime = Math.floor(Date.now() / 1000);
	return token.exp! - currentTime < TOKEN_REFRESH_MARGIN;
};

const LoginProvider = ({ children }: LoginProviderProps) => {
	const [state, setState] = useState<LoginState>(initialState);

	const {
		user,
		isAuthenticated,
		getAccessTokenSilently,
		getIdTokenClaims,
	} = useAuth0();

	const setRnResponseProjectId = useCallback((rnResponseProjectId: string | null) => {
		setState(prev => ({ ...prev, rnResponseProjectId }));
	}, []);

	const setAuth0IdToken = useCallback((auth0IdToken: string | null) => {
		setState(prev => ({ ...prev, auth0IdToken }));
	}, []);

	const setIsAppInitialized = useCallback((isAppInitialized: boolean) => {
		setState(prev => ({ ...prev, isAppInitialized }));
	}, []);

	const setSelectedConsumer = useCallback((selectedConsumer: string | null) => {
		setState(prev => ({ ...prev, selectedConsumer }));
		localStorage.setItem(`selectedConsumer`, selectedConsumer!);
	}, []);

	const setError = useCallback((error: Error | null) => {
		setState(prev => ({ ...prev, error }));
	}, []);

	const setSelectedConsumerId = useCallback((consumerId: string | null) => {
		setState(prev => ({ ...prev, consumerId }));
		localStorage.setItem(`consumerId`, consumerId!);
	}, []);

	const setIFrameQueryParam = useCallback((iFrameQueryParam: string | null) => {
		setState(prev => ({ ...prev, iFrameQueryParam }));
		localStorage.setItem(`iFrameQueryParam`, iFrameQueryParam!);
	}, []);


	const initConsumerToggleOptions = useCallback(async (auth0Token: string | null): Promise<ConsumerToggleResponse | null> => {
		try {
			const consumerToggleOptionsResponse = await getConsumerToggleOptions(auth0Token);
			if (consumerToggleOptionsResponse) {
				setState(prev => ({ ...prev, consumerToggleOptions: consumerToggleOptionsResponse.consumerToggleOptions }));
				setState(prev => ({ ...prev, selectedConsumer:  consumerToggleOptionsResponse.selectedConsumer}));
			} else {
				setState(prev => ({ ...prev, consumerToggleOptions: [] }));
			}
			return consumerToggleOptionsResponse;
		} catch (error) {
			console.error('Failed to initialize consumers:', error);
			setState(prev => ({ ...prev, consumerToggleOptions: [] }));
			throw error;
		}
	}, []);

	const refreshToken = useCallback(async () => {
		try {
			setState(prev => ({ ...prev, isLoading: true }));
			const newTokenResponse = await getAccessTokenSilently({
				detailedResponse: true,
				cacheMode: 'off'
			});
			setAuth0IdToken(newTokenResponse.id_token);
			setError(null);
		} catch (error) {
			setError(error instanceof Error ? error : new Error('Failed to refresh token'));
			console.error('Error refreshing token:', error);
		} finally {
			setState(prev => ({ ...prev, isLoading: false }));
		}
	}, [getAccessTokenSilently, setAuth0IdToken, setError]);

	// Token management, refreshes token if previous token expired
	useEffect(() => {
		const manageTokenAuthentication = async () => {
			if (!isAuthenticated) {
				setState(prev => ({
					...prev,
					auth0IdToken: null,
					isLoading: false
				}));
				return;
			}

			try {
				setState(prev => ({ ...prev, isLoading: true }));
				const idTokenClaims = await getIdTokenClaims();

				if (!idTokenClaims?.__raw) {
					throw new Error('No ID token claims available');
				}

				const decoded = jwtDecode(idTokenClaims.__raw);

				if (isTokenExpired(decoded)) {
					await refreshToken();
				} else if (shouldRefreshToken(decoded)) {
					// Schedule a refresh if token is close to expiring
					setTimeout(() => refreshToken(), 1000);
				} else {
					setAuth0IdToken(idTokenClaims.__raw);
				}
			} catch (error) {
				console.error('Error in token management:', error);
				setError(error instanceof Error ? error : new Error('Failed to manage token'));
				// Attempt one retry
				try {
					await refreshToken();
				} catch (retryError) {
					console.error('Retry failed:', retryError);
				}
			} finally {
				setState(prev => ({ ...prev, isLoading: false }));
			}
		};

		manageTokenAuthentication();
	}, [isAuthenticated, getIdTokenClaims, refreshToken, setAuth0IdToken, setError]);

	// Update user in state when auth0 user changes
	useEffect(() => {
		setState(prev => ({ ...prev, user }));
	}, [user]);

	const value: LoginContextType = {
		...state,
		setRnResponseProjectId,
		setAuth0IdToken,
		setIsAppInitialized,
		setSelectedConsumer,
		setSelectedConsumerId,
		refreshToken,
		initConsumerToggleOptions,
		setIFrameQueryParam
	};

	return (
		<LoginContext.Provider value={value}>
			{children}
		</LoginContext.Provider>
	);
};

export { LoginContext, LoginProvider };
export type { LoginContextType, LoginProviderProps };